/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/controller/useInboxState.ts
 * FILE_ID: EMAIL-INBOX-CONTROLLER-STATE-001
 * PURPOSE: Own route, search, filter, mailbox, preference, and selection state.
 */

/* eslint-disable react-hooks/exhaustive-deps -- Preserve pre-extraction hook dependencies. */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { getSamKanbanApi, resolveKanbanOrgId } from '@quotech/kanban-host'
import {
  fetchServerState,
  scheduleServerSave,
} from '@quotech/tenant-shared/preferences/dbViewPreference'
import { setEmailMailboxScope } from '@quotech/tenant-shared/email/api'
import { useApiAuth } from '../../../../_internal/components/ApiAuthGate'
import { useDebouncedValue } from '../../../../_internal/hooks/useDebouncedValue'
import {
  getQuotechOrgIdOverride,
  getRuntimeQuotechOrgId,
} from '../../../../_internal/services/quotechApi'
import { useRenderTiming } from '@quotech/ui'
import { useOrgCustomCategories, usePersonalCategories } from '../../../hooks/useEmailLabelPrefs'
import { useEmailAccounts } from '../../../hooks/useEmails'
import { useAiCopilotStore } from '../../../store/aiCopilotStore'
import { useEmailFilterStore, mailboxKey, splitMailboxKeys } from '../../../stores/emailFilterStore'
import { useCompanyScopeStore } from '../../../stores/companyScopeStore'
import { useMutedCategories } from '../../../stores/mutedCategoriesStore'
import { isSmartSearchEnabled, setSmartSearchEnabled } from '../../../lib/smartSearchFlag'
import { parseEmailSearchQuery, suggestEmailSearchTokens } from '../../../lib/parseEmailSearchQuery'
import { IMPORTANT_CATEGORIES } from '../../../lib/categoryConfig'
import { DEFAULT_EMAIL_SORT, isEmailSortMode, type EmailSortMode } from '../../../lib/sortEmails'
import type { InboxScope } from '../../../components/InboxScopeBar'
import { isComposePath } from '../../inboxComposeMode'
import { FOLDER_TITLES } from '../helpers'
import type { InboxPageProps } from '../contracts'
import { useReaderRouteSelection } from './useReaderRouteSelection'

const INBOX_TOGGLES_DB_SCOPE = 'email:inbox-toggles'

export function useInboxState({ folder = 'inbox' }: InboxPageProps = {}) {
  useRenderTiming('InboxPage')
  const isInboxFolder = folder === 'inbox'
  const isOutgoingFolder = folder === 'sent'
  const folderTitle = FOLDER_TITLES[folder]
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const scopeAll = isInboxFolder && searchParams.get('scope') === 'all'
  // FEAT-2026-07-26-INBOX-AI-REVIEW-FILTER. Derived from the URL rather than
  // useState, exactly like `scopeAll` above: the operator reaches this by
  // clicking a count, so it must survive a reload, deep-link and the back
  // button. A useState copy would also let the URL and the list disagree.
  const needsReviewOnly =
    isInboxFolder && searchParams.get('needs_review') === 'true'
  const toggleNeedsReviewOnly = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (next.get('needs_review') === 'true') next.delete('needs_review')
      else next.set('needs_review', 'true')
      return next
      // `replace` so repeatedly toggling the strip does not bury the page the
      // operator arrived from under a stack of history entries.
    }, { replace: true })
  }, [setSearchParams])
  const activeScope: InboxScope =
    folder === 'archived'
      ? 'done'
      : folder === 'binned'
        ? 'junk'
        : scopeAll
          ? 'all'
          : 'focus'
  const location = useLocation()
  const isComposeMode = useMemo(
    () => isComposePath(location.pathname),
    [location.pathname],
  )
  const composeKey = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const draft = params.get('draft')
    if (draft) return `draft:${draft}`
    const reply = params.get('reply')
    if (reply) return `reply:${reply}`
    const replyAll = params.get('reply_all')
    if (replyAll) return `reply_all:${replyAll}`
    const forward = params.get('forward')
    if (forward) return `forward:${forward}`
    return 'new'
  }, [location.search])
  const kanbanApi = useMemo(() => getSamKanbanApi(), [])
  const personalCategories = usePersonalCategories()
  const orgCustomCategories = useOrgCustomCategories()
  const customCategories = useMemo(
    () => [...personalCategories, ...orgCustomCategories],
    [personalCategories, orgCustomCategories],
  )
  const { session: kanbanSession } = useApiAuth()
  const kanbanOrgId = resolveKanbanOrgId(kanbanSession, {
    runtimeId: getRuntimeQuotechOrgId(),
    buildtimeId: getQuotechOrgIdOverride(),
  }) ?? null

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [smartSearchOn, setSmartSearchOn] = useState(() => isSmartSearchEnabled())
  const toggleSmartSearch = useCallback(() => {
    setSmartSearchOn((value) => {
      setSmartSearchEnabled(!value)
      return !value
    })
  }, [])
  const [searchFocused, setSearchFocused] = useState(false)
  const [searchHighlight, setSearchHighlight] = useState(0)
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 250)
  const liveSearchSuggestions = useMemo(
    () => suggestEmailSearchTokens(searchQuery),
    [searchQuery],
  )
  const parsedDebouncedSearch = useMemo(
    () => parseEmailSearchQuery(debouncedSearchQuery),
    [debouncedSearchQuery],
  )
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    () => (searchParams.get('category') || '').split(',').map((value) => value.trim()).filter(Boolean),
  )
  const toggleCategory = useCallback((key: string) => {
    setSelectedCategories((previous) =>
      previous.includes(key)
        ? previous.filter((category) => category !== key)
        : [...previous, key],
    )
  }, [])
  const applyImportantCategories = useCallback(() => {
    setSelectedCategories([...IMPORTANT_CATEGORIES])
  }, [])
  const [isReadFilter, setIsReadFilter] = useState<boolean | undefined>(
    searchParams.get('is_read') === 'true'
      ? true
      : searchParams.get('is_read') === 'false' ? false : undefined,
  )
  const [isStarredFilter, setIsStarredFilter] = useState<boolean | undefined>(
    searchParams.get('is_starred') === 'true'
      ? true
      : searchParams.get('is_starred') === 'false' ? false : undefined,
  )
  const [allocationFilter, setAllocationFilter] =
    useState<'allocated' | 'unallocated' | undefined>(() => {
      const value = searchParams.get('allocation')
      return value === 'allocated' || value === 'unallocated' ? value : undefined
    })
  const [jobIdFilter, setJobIdFilter] = useState<number | undefined>(() => {
    const value = searchParams.get('job_id')
    const parsed = value ? Number.parseInt(value, 10) : NaN
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
  })
  const [jobIdsIn, setJobIdsIn] = useState<number[]>([])
  const [fromAddressContains, setFromAddressContains] = useState('')
  // P23 — company filter lives in companyScopeStore so the reader, rows,
  // and CompanyContextDrawer share one domain + label without prop drilling.
  const companyDomain = useCompanyScopeStore((s) => s.companyDomain)
  const setCompanyDomain = useCompanyScopeStore((s) => s.setCompanyDomain)
  useEffect(() => {
    const fromUrl = (searchParams.get('company') || '').trim().toLowerCase()
    if (fromUrl && fromUrl !== useCompanyScopeStore.getState().companyDomain) {
      useCompanyScopeStore.getState().setCompanyDomain(fromUrl)
    }
  }, [searchParams])
  const [rightTab, setRightTab] = useState<'inbox' | 'ai'>('inbox')
  const copilotRequest = useAiCopilotStore((store) => store.request)
  const [copilotSeed, setCopilotSeed] =
    useState<{ text?: string; nonce: number } | null>(null)
  useEffect(() => useAiCopilotStore.getState().registerHost(), [])
  useEffect(() => {
    if (!copilotRequest) return
    setSelectedEmailId(copilotRequest.emailId)
    setRightTab('ai')
    setCopilotSeed({ text: copilotRequest.seed, nonce: copilotRequest.nonce })
    useAiCopilotStore.getState().consume()
  }, [copilotRequest])
  const [aiLinkOpen, setAiLinkOpen] = useState(false)
  const [receivedAtGte, setReceivedAtGte] = useState<string | undefined>()
  const [receivedAtLte, setReceivedAtLte] = useState<string | undefined>()
  const [hasAttachments, setHasAttachments] = useState<boolean | undefined>()
  const [hasCalendarEvent, setHasCalendarEvent] = useState<boolean | undefined>()
  const [mailboxKeysExplicit, setMailboxKeysExplicit] = useState<string[]>([])
  const { accounts: emailAccounts } = useEmailAccounts()
  const hiddenMailboxes = useEmailFilterStore((store) => store.hiddenMailboxes)
  const visibleMailboxKeys = useMemo(() => {
    if (!emailAccounts.length) return []
    const visible: string[] = []
    const visibleOwnerIds = new Set<number>()
    for (const account of emailAccounts) {
      const key = mailboxKey({
        account_id: account.account_id,
        user_id: account.owner_user_id,
      })
      if (key == null || hiddenMailboxes.has(key)) continue
      visible.push(key)
      if (typeof account.owner_user_id === 'number') {
        visibleOwnerIds.add(account.owner_user_id)
      }
    }
    for (const ownerId of visibleOwnerIds) {
      const legacyKey = `u:${ownerId}`
      if (!visible.includes(legacyKey)) visible.push(legacyKey)
    }
    return hiddenMailboxes.size > 0 ? visible : []
  }, [emailAccounts, hiddenMailboxes])
  const effectiveMailboxKeys =
    mailboxKeysExplicit.length > 0 ? mailboxKeysExplicit : visibleMailboxKeys
  const { accountIds: mailboxAccountIds, unattributedUserIds: mailboxLegacyUserIds } =
    useMemo(() => splitMailboxKeys(effectiveMailboxKeys), [effectiveMailboxKeys])

  // RL-01 — publish mailbox visibility scope during render so meta
  // caches and remount keys share org+mailbox identity with list queries
  // (which already send synced_by_account_id__in).
  const mailboxScope = useMemo(
    () =>
      effectiveMailboxKeys.length > 0
        ? [...effectiveMailboxKeys].sort().join('|')
        : '',
    [effectiveMailboxKeys],
  )
  const publishedMailboxScopeRef = useRef<string | undefined>(undefined)
  if (publishedMailboxScopeRef.current !== mailboxScope) {
    publishedMailboxScopeRef.current = mailboxScope
    setEmailMailboxScope(mailboxScope)
  }

  const [showSidebar, setShowSidebar] = useState(() => {
    try {
      return localStorage.getItem('email_inbox_show_filters') === '1'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem('email_inbox_show_filters', showSidebar ? '1' : '0')
    } catch { /* fail soft */ }
  }, [showSidebar])
  const togglesHydratedRef = useRef(false)
  const togglesTouchedRef = useRef(false)
  useEffect(() => {
    let cancelled = false
    void fetchServerState<{
      showFilters: boolean
      hidePromotions: boolean
      groupByMailbox?: boolean
      hideCleared?: boolean
    }>(INBOX_TOGGLES_DB_SCOPE, {
      showFilters: false,
      hidePromotions: true,
      groupByMailbox: false,
      hideCleared: true,
    }).then((saved) => {
      if (cancelled || togglesTouchedRef.current) {
        togglesHydratedRef.current = true
        return
      }
      if (saved) {
        if (typeof saved.showFilters === 'boolean') setShowSidebar(saved.showFilters)
        if (typeof saved.hidePromotions === 'boolean') setHidePromotions(saved.hidePromotions)
        if (typeof saved.groupByMailbox === 'boolean') setGroupByMailbox(saved.groupByMailbox)
        if (typeof saved.hideCleared === 'boolean') setHideCleared(saved.hideCleared)
      }
      togglesHydratedRef.current = true
    })
    return () => { cancelled = true }
  }, [])
  const [hidePromotions, setHidePromotions] = useStoredBoolean(
    'email_inbox_hide_promotions',
    true,
  )
  const [hideCleared, setHideCleared] = useStoredBoolean(
    'email_inbox_hide_cleared',
    true,
  )
  const [groupByMailbox, setGroupByMailbox] = useStoredBoolean(
    'email_inbox_group_mailbox',
    false,
  )
  const [sortMode, setSortMode] = useState<EmailSortMode>(() => {
    try {
      const saved = localStorage.getItem('email_inbox_sort_mode')
      return isEmailSortMode(saved) ? saved : DEFAULT_EMAIL_SORT
    } catch {
      return DEFAULT_EMAIL_SORT
    }
  })
  useEffect(() => {
    try { localStorage.setItem('email_inbox_sort_mode', sortMode) } catch { /* fail soft */ }
  }, [sortMode])
  const [shortcutsPanelOpen, setShortcutsPanelOpen] = useState(false)
  const [quickTriageEmailId, setQuickTriageEmailId] = useState<string | null>(null)
  const [allocateBoardEmailId, setAllocateBoardEmailId] = useState<string | null>(null)
  const shortcutsPanelOpenRef = useRef(false)
  useEffect(() => {
    // Suppress inbox hotkeys while ANY overlay that owns number keys is open
    // (cheat sheet OR Quick Triage palette).
    shortcutsPanelOpenRef.current =
      shortcutsPanelOpen || !!quickTriageEmailId || !!allocateBoardEmailId
  }, [allocateBoardEmailId, shortcutsPanelOpen, quickTriageEmailId])
  const [collapsedMailboxes, setCollapsedMailboxes] = useState<Set<string>>(() => {
    try {
      const parsed: unknown = JSON.parse(
        localStorage.getItem('email_inbox_collapsed_mailboxes') || '[]',
      )
      return new Set(
        Array.isArray(parsed)
          ? parsed.filter((value): value is string => typeof value === 'string')
          : [],
      )
    } catch {
      return new Set()
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem(
        'email_inbox_collapsed_mailboxes',
        JSON.stringify(Array.from(collapsedMailboxes)),
      )
    } catch { /* fail soft */ }
  }, [collapsedMailboxes])
  const toggleMailboxGroup = useCallback((groupKey: string) => {
    setCollapsedMailboxes((previous) => {
      const next = new Set(previous)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }, [])
  useEffect(() => {
    if (!togglesHydratedRef.current) return
    togglesTouchedRef.current = true
    scheduleServerSave(INBOX_TOGGLES_DB_SCOPE, {
      showFilters: showSidebar,
      hidePromotions,
      groupByMailbox,
      hideCleared,
    })
  }, [showSidebar, hidePromotions, groupByMailbox, hideCleared])
  const {
    selectedEmailId,
    setSelectedEmailId,
    openSelectedEmailId,
    closeSelectedEmail,
  } = useReaderRouteSelection()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const mutedCategories = useMutedCategories()

  return {
    folder, isInboxFolder, isOutgoingFolder, folderTitle, navigate, searchParams,
    setSearchParams, scopeAll, needsReviewOnly, toggleNeedsReviewOnly,
    activeScope, location, isComposeMode, composeKey,
    kanbanApi, customCategories, kanbanSession, kanbanOrgId, searchQuery,
    setSearchQuery, smartSearchOn, toggleSmartSearch, searchFocused,
    setSearchFocused, searchHighlight, setSearchHighlight, debouncedSearchQuery,
    liveSearchSuggestions, parsedDebouncedSearch, selectedCategories,
    setSelectedCategories, toggleCategory, applyImportantCategories,
    isReadFilter, setIsReadFilter,
    isStarredFilter, setIsStarredFilter, allocationFilter, setAllocationFilter,
    jobIdFilter, setJobIdFilter, jobIdsIn, setJobIdsIn, fromAddressContains,
    setFromAddressContains, companyDomain, setCompanyDomain, rightTab, setRightTab,
    copilotSeed, aiLinkOpen, setAiLinkOpen, receivedAtGte, setReceivedAtGte,
    receivedAtLte, setReceivedAtLte, hasAttachments, setHasAttachments,
    hasCalendarEvent, setHasCalendarEvent, mailboxKeysExplicit,
    setMailboxKeysExplicit, emailAccounts, hiddenMailboxes, mailboxAccountIds,
    mailboxLegacyUserIds, showSidebar, setShowSidebar, hidePromotions,
    setHidePromotions, hideCleared, setHideCleared, groupByMailbox,
    setGroupByMailbox, sortMode, setSortMode, shortcutsPanelOpen,
    setShortcutsPanelOpen, shortcutsPanelOpenRef, quickTriageEmailId,
    setQuickTriageEmailId, allocateBoardEmailId, setAllocateBoardEmailId,
    collapsedMailboxes,
    toggleMailboxGroup, selectedEmailId, setSelectedEmailId,
    openSelectedEmailId, closeSelectedEmail, selectedIds,
    setSelectedIds, bulkLinkOpen, setBulkLinkOpen, listRef, mutedCategories,
  }
}

function useStoredBoolean(key: string, defaultValue: boolean) {
  const [value, setValue] = useState(() => {
    try {
      const saved = localStorage.getItem(key)
      return saved === null ? defaultValue : saved === '1'
    } catch {
      return defaultValue
    }
  })
  useEffect(() => {
    try { localStorage.setItem(key, value ? '1' : '0') } catch { /* fail soft */ }
  }, [key, value])
  return [value, setValue] as const
}
